import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Segment, Dimmer, Loader, Table, Pagination, Button, Label, Icon } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';

import {
  SG_GET_ACTION_TEMPLATES,
} from "constants/actions";
import PageIntro from '../components/PageIntro';

const EmailTemplateConfigList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const { templates, loading: reduxLoading } = useSelector((state) => ({
    templates: state.action_template?.templates || null,
    loading: state.action_template?.pending || false,
  }));

  useEffect(() => {
    setLoading(true);
    dispatch({
      type: SG_GET_ACTION_TEMPLATES,
      payload: `page=${page}`,
    });
  }, [dispatch, page]);

  useEffect(() => {
    if (templates !== null) {
      setLoading(false);
    }
  }, [templates]);

  useEffect(() => {
    if (reduxLoading !== undefined) {
      setLoading(reduxLoading);
    }
  }, [reduxLoading]);

  const handlePageChange = (event, { activePage }) => {
    setPage(activePage);
  };

  const handleNewTemplate = () => {
    navigate('new');
  };

  const handleEditTemplate = (id) => {
    navigate(`edit/${id}`);
  };

  const getStatusColor = (templateStatus, isActive) => {
    if (!isActive) return 'grey';
    switch (templateStatus) {
      case 'verified': return 'green';
      case 'unverified': return 'orange';
      case 'needs_sync': return 'yellow';
      default: return 'grey';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div>
      <PageIntro
        title="Email Template Configurations"
        description="Configure organization-level email templates (Postmark) used for report delivery, invites and reminders."
        bullets={[
          'Where data comes from: template configurations map email types to Postmark templates.',
          'What to expect: verification status, template ids, and organization scoping.',
          'Next steps: create or verify templates to ensure emails send correctly.'
        ]}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Email Template Configurations</h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>Manage organization email template configurations</p>
        </div>
        <Button primary onClick={handleNewTemplate}>
          <Icon name="plus" />
          New Email Template Configuration
        </Button>
      </div>

      {loading ? (
        <Segment style={{ minHeight: '300px' }}>
          <Dimmer active>
            <Loader>Loading email template configurations...</Loader>
          </Dimmer>
        </Segment>
      ) : (
        <>
          {templates && templates.results && templates.results.length > 0 ? (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={3}>Email Type</Table.HeaderCell>
                  <Table.HeaderCell width={3}>Organization</Table.HeaderCell>
                  <Table.HeaderCell width={3}>Template Details</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Status</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Language</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Last Updated</Table.HeaderCell>
                  <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {templates.results.map((config) => (
                  <Table.Row key={config.id}>
                    <Table.Cell>
                      <div>
                        <strong>{config.email_type_display || config.email_type}</strong>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem' }}>
                          ID: {config.id}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="building" style={{ marginRight: '0.5rem' }} />
                        <span>{config.organization ? config.organization.name : 'No Organization'}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <div style={{ marginBottom: '0.25rem' }}>
                          <Icon name="mail" />
                          <strong>ID:</strong> {config.postmark_template_id || 'None'}
                        </div>
                        {config.postmark_template_alias && (
                          <div style={{ marginBottom: '0.25rem' }}>
                            <Icon name="tag" />
                            <strong>Alias:</strong> {config.postmark_template_alias}
                          </div>
                        )}
                        {config.template_name && (
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            <strong>Name:</strong> {config.template_name}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <div>
                        <Label 
                          color={getStatusColor(config.template_status, config.is_active)}
                          size="small"
                        >
                          {config.is_active ? config.template_status || 'unverified' : 'inactive'}
                        </Label>
                        <div style={{ fontSize: '0.75em', marginTop: '0.25rem', color: '#666' }}>
                          {config.is_verified ? 'Verified' : 'Not Verified'}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Label size="small" basic>
                        {config.language.toUpperCase()}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ fontSize: '0.85em' }}>
                        <div><strong>Updated:</strong></div>
                        <div>{formatDate(config.updated_at)}</div>
                        <div style={{ marginTop: '0.25rem', color: '#666' }}>
                          <strong>Created:</strong> {formatDate(config.created_at)}
                        </div>
                        {config.template_last_synced && (
                          <div style={{ marginTop: '0.25rem', color: '#666' }}>
                            <strong>Synced:</strong> {formatDate(config.template_last_synced)}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Button
                          size="mini"
                          primary
                          onClick={() => handleEditTemplate(config.id)}
                          fluid
                        >
                          <Icon name="edit" />
                          Edit
                        </Button>
                        <Button
                          size="mini"
                          onClick={() => navigate(`details/${config.id}`)}
                          fluid
                        >
                          <Icon name="eye" />
                          Details
                        </Button>
                        {!config.is_verified && (
                          <Button
                            size="mini"
                            color="orange"
                            onClick={() => navigate(`verify/${config.id}`)}
                            fluid
                          >
                            <Icon name="checkmark" />
                            Verify
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Segment placeholder>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Icon name="mail outline" size="huge" style={{ color: '#ccc', marginBottom: '1rem' }} />
                <h3>No email template configurations found</h3>
                <p>Click "New Email Template Configuration" to create your first configuration.</p>
                <Button primary onClick={handleNewTemplate} style={{ marginTop: '1rem' }}>
                  <Icon name="plus" />
                  Create First Configuration
                </Button>
              </div>
            </Segment>
          )}

          {templates && templates.count > (templates.page_size || 10) && (
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Pagination
                activePage={page}
                totalPages={Math.ceil(templates.count / (templates.page_size || 10))}
                onPageChange={handlePageChange}
                size="small"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailTemplateConfigList;
